import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AmazonCarrierIcon from "@redotech/redo-icons/carriers/amazon.svg";
import DhlCarrierIcon from "@redotech/redo-icons/carriers/dhl.svg";
import UpsCarrierIcon from "@redotech/redo-icons/carriers/ups.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FeatureOnboardingType } from "@redotech/redo-model/onboarding/user-onboarding";
import { CarrierQuoteResult } from "@redotech/redo-model/outbound-labels/pricing-calculator";
import {
  RateTableCarrier,
  rateTableCarrierLabel,
} from "@redotech/redo-model/rate-table/rate-table-mapping";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { OnboardingProps } from "../common";
import * as styles from "./outbound-labels-upsell.module.css";

const CALENDAR_LINK =
  "https://calendly.com/d/csp9-x7r-5g6/redo-oms-rate-optimization";

const CARRIER_FOR_TYPE: Partial<
  Record<FeatureOnboardingType, RateTableCarrier>
> = {
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS]: RateTableCarrier.UPS,
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON]:
    RateTableCarrier.Amazon,
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL]: RateTableCarrier.DHL_ECS,
};

function getCarrierIcon(carrier: RateTableCarrier) {
  const props = { height: 64, width: 64 };
  switch (carrier) {
    case RateTableCarrier.UPS:
      return <UpsCarrierIcon {...props} />;
    case RateTableCarrier.DHL_ECS:
      return <DhlCarrierIcon {...props} />;
    case RateTableCarrier.Amazon:
      return <AmazonCarrierIcon {...props} />;
    default:
      return null;
  }
}

interface UpsellModalProps {
  bestResult: CarrierQuoteResult;
  teamId: string;
  handleDismiss: () => void;
}

const UpsellModal = memo(function UpsellModal({
  bestResult,
  teamId,
  handleDismiss,
}: UpsellModalProps) {
  const client = useRequiredContext(RedoMerchantRpcClientContext);

  const avgPerOrder =
    Math.round(bestResult.avgMerchantSavingsPerOrder * 100) / 100;
  const monthlySavings = Math.round(bestResult.monthlySavings);
  const annualSavings = Math.round(bestResult.annualSavings);
  const bestCarrier = bestResult.carrier;
  const carrierName = rateTableCarrierLabel(bestCarrier);

  const handleSetUpNow = useHandler(async () => {
    amplitude.logEvent("outbound-labels-upsell-schedule-clicked", {
      teamId,
      monthlySavings,
      bestCarrier,
    });
    window.open(CALENDAR_LINK, "_blank");
    try {
      await client.acceptLabelsUpsell({
        annualRedoProfit: bestResult.annualRedoProfit,
        carrier: bestCarrier,
      });
    } catch (error) {
      console.error("Failed to record carrier acceptance:", error);
    }
  });

  const handleClose = useHandler(() => {
    void handleDismiss?.();
  });

  return (
    <RedoModal
      hideFooter
      hideHeader
      isOpen
      onModalCloseRequested={handleClose}
      title=""
    >
      <Flex dir="column" gap="2xl" pt="3xl">
        {getCarrierIcon(bestCarrier)}
        <Flex dir="column" gap="sm">
          <Text fontSize="lg" fontWeight="semibold">
            Swap to {carrierName} and save.
          </Text>
          <Text fontSize="sm" textColor="secondary">
            Here is a look at how much you could have saved by shipping with{" "}
            {carrierName}.
          </Text>
        </Flex>

        <div className={styles.statsRow}>
          <div className={styles.statItem}>
            <Text fontSize="sm" textColor="secondary">
              Per order
            </Text>
            <Text fontSize="xl" fontWeight="semibold" textColor="success">
              ${avgPerOrder.toFixed(2)}
            </Text>
          </div>
          <div className={styles.statItem}>
            <Text fontSize="sm" textColor="secondary">
              Per month
            </Text>
            <Text fontSize="xl" fontWeight="semibold" textColor="success">
              ${monthlySavings.toLocaleString()}
            </Text>
          </div>
          <div className={styles.statItem}>
            <Text fontSize="sm" textColor="secondary">
              Per year
            </Text>
            <Text fontSize="xl" fontWeight="semibold" textColor="success">
              ${annualSavings.toLocaleString()}
            </Text>
          </div>
        </div>
      </Flex>

      <Flex justify="flex-end" py="3xl">
        <RedoButton
          hierarchy="primary"
          onClick={handleSetUpNow}
          size="sm"
          text={`Start saving with ${carrierName} on Redo`}
          theme="normal"
        />
      </Flex>
    </RedoModal>
  );
});

interface OutboundLabelsUpsellOnboardingProps extends OnboardingProps {
  carrier: FeatureOnboardingType;
}

export const OutboundLabelsUpsellOnboarding = memo(
  function OutboundLabelsUpsellOnboarding({
    carrier: carrierType,
    handleDismiss,
  }: OutboundLabelsUpsellOnboardingProps) {
    const team = useRequiredContext(TeamContext);
    const upsell = team.settings.outboundLabels?.outboundLabelsUpsell;
    const carrier = CARRIER_FOR_TYPE[carrierType];

    const acceptedCarriers = upsell?.acceptedCarriers ?? [];
    const acceptedCarrierProfit = upsell?.acceptedCarrierProfit ?? 0;

    // Check if carrier integration is already enabled
    const isCarrierIntegrationEnabled =
      carrier === RateTableCarrier.DHL_ECS
        ? team.settings.outboundLabels?.enabledCarrierAccounts
            ?.directToCarrierIntegrations?.dhl
        : carrier === RateTableCarrier.Amazon
          ? team.settings.outboundLabels?.useAmazonCarrierAccount
          : false;

    const bestResult = carrier
      ? upsell?.carrierResults?.find(
          (r) =>
            r.carrier === carrier &&
            !acceptedCarriers.includes(r.carrier) &&
            r.annualRedoProfit > acceptedCarrierProfit &&
            !isCarrierIntegrationEnabled,
        )
      : undefined;

    if (!bestResult) return null;

    return (
      <UpsellModal
        bestResult={bestResult}
        handleDismiss={handleDismiss}
        teamId={team._id.toString()}
      />
    );
  },
);
